import {
  Directive,
  Input,
  TemplateRef,
  ViewContainerRef,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { ScreenService } from './screen.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ScreenState } from './screen-size-info.interface';

/**
 * Directiva estructural para mostrar/ocultar contenido basado en el breakpoint
 *
 * @example
 * ```html
 * <div *appShowOnScreen="'mobile'">Solo visible en móvil</div>
 * <div *appShowOnScreen="['tablet', 'desktop']">Visible en tablet y desktop</div>
 * ```
 */
@Directive({
  selector: '[appShowOnScreen]',
  standalone: true,
})
export class ShowOnScreenDirective implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private hasView = false;

  @Input('appShowOnScreen') screenStates: ScreenState | ScreenState[] = [];

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private screenService: ScreenService
  ) {}

  ngOnInit() {
    this.screenService.screenInfo$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateView();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateView() {
    const currentState = this.screenService.screenState();
    const statesToShow = Array.isArray(this.screenStates)
      ? this.screenStates
      : [this.screenStates];
    const shouldShow = statesToShow.includes(currentState);

    if (shouldShow && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!shouldShow && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}

/**
 * Directiva estructural para ocultar contenido basado en el breakpoint
 *
 * @example
 * ```html
 * <div *appHideOnScreen="'mobile'">Oculto en móvil</div>
 * <div *appHideOnScreen="['mobile', 'tablet']">Oculto en móvil y tablet</div>
 * ```
 */
@Directive({
  selector: '[appHideOnScreen]',
  standalone: true,
})
export class HideOnScreenDirective implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private hasView = true;

  @Input('appHideOnScreen') screenStates: ScreenState | ScreenState[] = [];

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private screenService: ScreenService
  ) {}

  ngOnInit() {
    this.viewContainer.createEmbeddedView(this.templateRef);

    this.screenService.screenInfo$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateView();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateView() {
    const currentState = this.screenService.screenState();
    const statesToHide = Array.isArray(this.screenStates)
      ? this.screenStates
      : [this.screenStates];
    const shouldHide = statesToHide.includes(currentState);

    if (shouldHide && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    } else if (!shouldHide && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    }
  }
}
